/**
 * Managed Video — global silent-autoplay behavior for all THEME videos.
 *
 * Applies to every decorative `<video autoplay>` and `<video data-managed-autoplay>` on the page
 * (homepage, banners, page sections, rich-text/video, lifestyle, storytelling, and any future
 * section) EXCEPT Shopify's native product media (PDP gallery + product cards), which keeps its
 * native behavior untouched.
 *
 * Behavior:
 *  - autoplay muted + loop + inline; PLAY when in viewport, PAUSE when out (one shared observer).
 *  - prefers-reduced-motion: do NOT autoplay — show the poster, let the visitor play manually.
 *  - a discreet, keyboard-accessible play/pause control (WCAG 2.2 SC 2.2.2 "Pause, Stop, Hide").
 *  - session preference: once the visitor Pauses, other autoplay videos stay paused for the session
 *    (sessionStorage — not persisted across sessions) until they press Play.
 *  - deferred download: a video shipped with `data-managed-autoplay` + `preload="none"` (see
 *    sections/hero.liquid) fetches NOTHING at parse time. A second observer lifts preload and
 *    starts the fetch only as it approaches the viewport; playback still begins at the same 0.2
 *    threshold. Everything above still decides WHETHER it plays.
 *
 * Architecture: ONE controller, TWO IntersectionObservers (play/pause + download arming), ONE
 * reduced-motion query, ONE session key.
 * Additive — no edits to Horizon's media.js / video-background.js.
 */

const SESSION_KEY = 'managed-video:paused';
const HINT_MS = 2500;
// Native Shopify product media — never touch these.
const EXCLUDE_SELECTOR = '.product-media-container, .card-gallery, media-gallery, product-model';
/**
 * `video[autoplay]` is the original contract: a section ships the attribute, this controller takes
 * it over. `video[data-managed-autoplay]` is the deferred-download variant: the section ships NO
 * autoplay attribute and `preload="none"` instead, so the browser fetches zero media bytes at parse
 * time, and this controller starts the download as the video approaches the viewport (see #arm).
 * Both forms get identical playback, control, reduced-motion and session-preference behavior.
 */
const VIDEO_SELECTOR = 'video[autoplay], video[data-managed-autoplay]';
/**
 * How far ahead of the viewport a deferred video starts downloading. Large enough that the fetch
 * has a head start before the play threshold is crossed, small enough that a video far down the
 * page still costs nothing on load.
 */
const LOAD_MARGIN = '400px';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const readPaused = () => {
  try {
    return sessionStorage.getItem(SESSION_KEY) === 'true';
  } catch {
    return false;
  }
};
const writePaused = (value) => {
  try {
    sessionStorage.setItem(SESSION_KEY, value ? 'true' : 'false');
  } catch {
    /* private mode / storage disabled — behavior just isn't remembered */
  }
};

/** @type {HTMLTemplateElement | null} */
let controlTemplate = null;

/** @type {Set<ManagedVideo>} */
const instances = new Set();
/** @type {WeakMap<HTMLVideoElement, ManagedVideo>} */
const byVideo = new WeakMap();

// Session-wide "the visitor prefers videos paused" flag (governs autoplay, not manual play).
let globalPaused = readPaused();

// ONE shared observer for every managed video.
const observer = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) byVideo.get(/** @type {HTMLVideoElement} */ (entry.target))?.setInView(entry.isIntersecting);
  },
  { threshold: 0.2 }
);

/**
 * A SECOND, separate observer that only arms the download for `preload="none"` videos. It is
 * deliberately not merged into the playback observer above: that one's threshold (0.2, no margin)
 * defines play/pause for every video in the theme and must not move. This one fires earlier
 * (LOAD_MARGIN ahead) and does nothing but start the fetch.
 *
 * Videos inside a `display: none` subtree — which is how Hero switches between its desktop and
 * mobile encodes — never intersect, so this never fires for them and the non-applicable source
 * stays at zero bytes.
 */
const loadObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) byVideo.get(/** @type {HTMLVideoElement} */ (entry.target))?.arm();
    }
  },
  { rootMargin: LOAD_MARGIN, threshold: 0 }
);

/** @param {boolean} paused */
function setGlobalPaused(paused) {
  globalPaused = paused;
  writePaused(paused);
  for (const mv of instances) mv.onPreferenceChanged();
}

class ManagedVideo {
  /** @param {HTMLVideoElement} video */
  constructor(video) {
    this.video = video;
    this.inView = false;
    // Autoplay is governed by the session preference; reduced-motion suppresses it entirely.
    this.desired = !reduceMotion.matches && !globalPaused;

    // Enforce silent, inline, looping decorative playback; the controller owns play/pause.
    video.muted = true;
    video.loop = true;
    video.setAttribute('playsinline', '');
    video.removeAttribute('controls');
    // Take over autoplay so reduced-motion / paused-preference are honored and playback is
    // viewport-gated. (Removing the attribute also makes "JS failed to load" degrade to no-autoplay,
    // which is the ADA-safe fallback.)
    video.removeAttribute('autoplay');

    this.host = video.parentElement;
    this.host?.classList.add('managed-video-host');

    this.control = this.#buildControl();
    if (this.control) video.insertAdjacentElement('afterend', this.control);

    this.#onPlay = () => {
      this.#hint();
      this.#reflectButton();
    };
    this.#onPauseOrRetry = () => this.#reflectButton();
    this.#onCanPlay = () => this.#update();
    video.addEventListener('play', this.#onPlay);
    video.addEventListener('pause', this.#onPauseOrRetry);
    video.addEventListener('loadeddata', this.#onCanPlay);

    // Mobile: reveal the control after the first tap, and keep it accessible.
    this.#onPointerDown = (/** @type {PointerEvent} */ event) => {
      if (event.pointerType === 'touch') this.host?.classList.add('managed-video-host--touched');
    };
    this.host?.addEventListener('pointerdown', this.#onPointerDown, { passive: true });

    byVideo.set(video, this);
    instances.add(this);
    // The observer's initial callback drives the first play/pause (avoids a play→pause blip for a
    // video that's already autoplaying and in view). Just set the initial button state here.
    observer.observe(video);
    // Only videos that shipped deferred need arming. Anything already fetching (the original
    // `video[autoplay]` contract) is left completely alone.
    if (video.preload === 'none') loadObserver.observe(video);
    this.#reflectButton();
  }

  /**
   * Lift `preload="none"` and begin the fetch. Runs once, as the video approaches the viewport.
   *
   * A visitor who won't get autoplay — prefers-reduced-motion, or the session pause preference —
   * doesn't get the download either; for them the file is fetched on demand by play() when they
   * press the control, which overrides preload. We stay observed in that case so the download can
   * still be armed if they clear the preference and scroll past again.
   */
  arm() {
    if (this.#armed || !this.desired) return;
    this.#disarm();
    this.video.preload = 'auto';
    // Changing `preload` alone does not restart a suspended resource selection; load() does.
    // Safe here because #disarm() guarantees it can never run against a playing video.
    this.video.load();
  }

  /** Retire this video from the arming observer, permanently. */
  #disarm() {
    if (this.#armed) return;
    this.#armed = true;
    loadObserver.unobserve(this.video);
  }

  /** @returns {HTMLButtonElement | null} */
  #buildControl() {
    if (!controlTemplate) return null;
    const fragment = /** @type {DocumentFragment} */ (controlTemplate.content.cloneNode(true));
    const button = fragment.querySelector('button');
    if (!(button instanceof HTMLButtonElement)) return null;
    this.playLabel = button.dataset.labelPlay || 'Play video';
    this.pauseLabel = button.dataset.labelPause || 'Pause video';
    button.addEventListener('click', () => this.#toggle());
    return button;
  }

  /** @param {boolean} inView */
  setInView(inView) {
    this.inView = inView;
    this.#update();
  }

  // A global Pause/Play elsewhere changed the session preference.
  onPreferenceChanged() {
    this.desired = globalPaused ? false : !reduceMotion.matches;
    this.#update();
  }

  #toggle() {
    if (this.#isPlaying()) {
      // Pause → remember for the session; every managed video stops.
      setGlobalPaused(true);
    } else {
      // Play → clear the preference (others resume when in view), and play THIS one now (this is
      // also how a reduced-motion visitor opts into a specific video).
      setGlobalPaused(false);
      this.desired = true;
      this.#update();
    }
  }

  #isPlaying() {
    return !this.video.paused && !this.video.ended;
  }

  #update() {
    const shouldPlay = this.desired && this.inView && !document.hidden;
    if (shouldPlay) {
      // play() fetches on its own regardless of `preload`, so once we reach here the arming
      // observer has no work left — and must not fire load() into an already-playing video.
      this.#disarm();
      const played = this.video.play();
      if (played && typeof played.catch === 'function') played.catch(() => {});
    } else {
      this.video.pause();
    }
    this.#reflectButton();
  }

  #reflectButton() {
    if (!this.control) return;
    const playing = this.#isPlaying();
    this.control.classList.toggle('is-playing', playing);
    this.control.setAttribute('aria-label', playing ? this.pauseLabel : this.playLabel);
  }

  #hint() {
    if (!this.host) return;
    this.host.classList.add('managed-video-host--hint');
    clearTimeout(this.#hintTimer);
    this.#hintTimer = setTimeout(() => this.host?.classList.remove('managed-video-host--hint'), HINT_MS);
  }

  update() {
    this.#update();
  }

  destroy() {
    observer.unobserve(this.video);
    loadObserver.unobserve(this.video);
    this.video.removeEventListener('play', this.#onPlay);
    this.video.removeEventListener('pause', this.#onPauseOrRetry);
    this.video.removeEventListener('loadeddata', this.#onCanPlay);
    this.host?.removeEventListener('pointerdown', this.#onPointerDown);
    clearTimeout(this.#hintTimer);
    this.control?.remove();
    this.host?.classList.remove('managed-video-host', 'managed-video-host--hint', 'managed-video-host--touched');
    instances.delete(this);
    byVideo.delete(this.video);
  }

  /** @type {boolean} */
  #armed = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hintTimer;
  /** @type {() => void} */
  #onPlay;
  /** @type {() => void} */
  #onPauseOrRetry;
  /** @type {() => void} */
  #onCanPlay;
  /** @type {(event: PointerEvent) => void} */
  #onPointerDown;
}

/** @param {HTMLVideoElement} video */
function enhance(video) {
  if (byVideo.has(video)) return;
  if (video.dataset.nativeVideo != null) return; // explicit opt-out
  if (video.closest(EXCLUDE_SELECTOR)) return; // native Shopify product media
  new ManagedVideo(video);
}

/** Diagnostic warning is emitted once per page, not once per video. @type {boolean} */
let warnedNoTemplate = false;

/** @param {ParentNode} [root] */
function scan(root = document) {
  if (!controlTemplate) {
    const el = document.getElementById('managed-video-control-template');
    if (el instanceof HTMLTemplateElement) controlTemplate = el;
  }

  const videos = root.querySelectorAll(VIDEO_SELECTOR);

  // Without the template there is no Play/Pause button, so autoplaying decorative video runs
  // with no way to stop it — a WCAG 2.2 SC 2.2.2 failure that is invisible in review because
  // everything else still looks and behaves correctly. Never let that happen quietly.
  if (!controlTemplate && videos.length && !warnedNoTemplate) {
    warnedNoTemplate = true;
    console.warn(
      '[managed-video] #managed-video-control-template not found, but %d managed video(s) are on ' +
        'the page. They will play with NO pause control (WCAG 2.2.2 "Pause, Stop, Hide" failure). ' +
        "Restore {%% render 'managed-video' %%} in layout/theme.liquid.",
      videos.length
    );
  }

  videos.forEach((v) => enhance(/** @type {HTMLVideoElement} */ (v)));
}

// Keep in sync with system settings + tab visibility (no extra per-video listeners).
reduceMotion.addEventListener('change', () => {
  for (const mv of instances) mv.onPreferenceChanged();
});
document.addEventListener('visibilitychange', () => {
  for (const mv of instances) mv.update();
});

// Lazy: run once the DOM is ready. Also handle the Theme Editor adding/removing sections.
function init() {
  scan();
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
document.addEventListener('shopify:section:load', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  if (target) scan(target);
});
document.addEventListener('shopify:section:unload', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  target?.querySelectorAll(VIDEO_SELECTOR).forEach((v) => byVideo.get(/** @type {HTMLVideoElement} */ (v))?.destroy());
});
